import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight, Handshake, Users, DollarSign, Zap, BookOpen, HeadphonesIcon } from 'lucide-react'
import Seo from '../components/Seo'

const partnerTypes = [
  {
    title: 'Reseller Partners',
    description: 'Sell PrismScope as part of your solution stack. Earn recurring revenue on every deal you close.',
    benefits: [
      'Recurring revenue share on all referred accounts',
      'Co-branded campaigns and landing pages',
      'Deal registration and pipeline protection',
      'Dedicated partner account manager',
    ],
    cta: 'Become a Reseller',
    color: 'border-primary-500',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
    icon: DollarSign,
  },
  {
    title: 'Channel Partners',
    description: 'Integrate PrismScope into your service delivery. Offer conversational surveys as part of your consulting, CX, or research practice.',
    benefits: [
      'White-label options for your client engagements',
      'Volume pricing for multi-client deployments',
      'Joint go-to-market planning and materials',
      'Early access to new features and verticals',
    ],
    cta: 'Explore Channel Partnership',
    color: 'border-accent-500',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    icon: Handshake,
  },
  {
    title: 'Referral Partners',
    description: 'Know someone who needs better customer insights? Refer them to PrismScope and earn a commission on every closed deal.',
    benefits: [
      'Simple referral tracking and payouts',
      'No sales commitment required',
      'Commission on first-year contract value',
      'Marketing materials to share with your network',
    ],
    cta: 'Join the Referral Program',
    color: 'border-prism-emerald',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-prism-emerald',
    icon: Users,
  },
]

const whyPartner = [
  {
    icon: Zap,
    title: 'Growing market',
    description: 'Conversational AI for research is a fast-growing category. Get in early with a proven platform.',
  },
  {
    icon: DollarSign,
    title: 'Recurring revenue',
    description: 'Subscription-based model means ongoing commissions, not one-time payouts.',
  },
  {
    icon: BookOpen,
    title: 'Enablement and training',
    description: 'We invest in your success with onboarding, certification, and ongoing support.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Dedicated support',
    description: 'Every partner gets a dedicated contact. We co-sell, co-deliver, and co-succeed.',
  },
]

const idealPartners = [
  'Management consulting firms',
  'Market research agencies',
  'Customer experience consultancies',
  'Digital transformation partners',
  'HR and employee engagement platforms',
  'CRM and martech resellers',
]

export default function Partners() {
  return (
    <div>
      <Seo
        title="Partner Program"
        description="Partner with PrismScope — reseller, referral, and technology partnerships for the feedback-to-action platform."
        path="/partners"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-intelligence-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Partners</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-intelligence-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-primary-500/15 border border-primary-500/25 rounded-full text-primary-400 text-sm font-semibold mb-6">
            Partner Program
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
            Grow with PrismScope
          </h1>
          <p className="text-lg text-intelligence-400 max-w-2xl mx-auto mb-8">
            Join our partner ecosystem and help organizations unlock deeper customer insights.
            Earn recurring revenue while delivering real value to your clients.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
          >
            Talk to Partnerships <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Partner Types */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Choose your partnership model
            </h2>
            <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
              Whether you sell, integrate, or refer, we have a program that fits.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {partnerTypes.map((partner) => (
              <div
                key={partner.title}
                className={`bg-white rounded-xl border-t-4 ${partner.color} border border-intelligence-200 p-8 flex flex-col`}
              >
                <div className={`w-12 h-12 ${partner.iconBg} rounded-lg flex items-center justify-center mb-4`}>
                  <partner.icon className={`w-6 h-6 ${partner.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-intelligence-900 mb-3">{partner.title}</h3>
                <p className="text-intelligence-600 mb-6">{partner.description}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {partner.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start text-sm text-intelligence-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 mr-3 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {partner.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Why partner with PrismScope
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyPartner.map((item) => (
              <div key={item.title} className="bg-white rounded-xl border border-intelligence-200 p-6">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-intelligence-900 mb-2">{item.title}</h3>
                <p className="text-sm text-intelligence-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Partners */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Who we partner with
            </h2>
            <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
              Our best partners are already having conversations about customer insight, employee engagement, or market research with their clients.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {idealPartners.map((partner) => (
              <div
                key={partner}
                className="flex items-center bg-intelligence-50 border border-intelligence-200 rounded-lg p-4"
              >
                <span className="w-2 h-2 rounded-full bg-primary-500 mr-4 flex-shrink-0" />
                <span className="text-intelligence-800 font-medium">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-600 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to partner?
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Tell us about your business and we&apos;ll find the right partnership model for you.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-primary-600 bg-white hover:bg-primary-50 rounded-lg shadow-md transition-colors"
          >
            Start the Conversation <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  )
}
